import React, { useState, useRef, useEffect, CSSProperties } from 'react';
import { FaQuestionCircle } from 'react-icons/fa';

interface TooltipProps {
  text: string;
  iconSize?: string | number;
  iconColorClass?: string;
  textColorClass?: string;
  bgColorClass?: string;
}

export const TooltipElement = ({
  text,
  iconSize = '1em',
  iconColorClass = 'text-black hover:text-gray-600',
  textColorClass = 'text-white',
  bgColorClass = 'bg-neutral-600',
}: TooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipStyles, setTooltipStyles] = useState<CSSProperties>({
    opacity: 0,
    position: 'fixed',
    top: '-9999px', // Start off-screen
    left: '-9999px',
    zIndex: 50,
  });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const showTooltip = () => {
    if (!text || !text.trim()) return;
    setIsVisible(true);
  };
  const hideTooltip = () => {
    setIsVisible(false);
  };

  useEffect(() => {
    if (isVisible && tooltipRef.current && triggerRef.current && text && text.trim()) {
      const triggerElement = triggerRef.current;
      const tooltipElement = tooltipRef.current;

      const tRect = triggerElement.getBoundingClientRect();
      const ttRect = tooltipElement.getBoundingClientRect(); // Tooltip dimensions

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const margin = 8; // 8px margin from viewport edges or trigger

      let newTop: number;
      let newLeft: number;

      const desiredTopAbove = tRect.top - ttRect.height - margin;
      const desiredLeftCentered = tRect.left + (tRect.width / 2) - (ttRect.width / 2);

      newTop = desiredTopAbove;
      newLeft = desiredLeftCentered;

      if (newTop < margin) { // Check top boundary, if overflows, try below
        newTop = tRect.bottom + margin;
      }

      // Check bottom boundary (especially if flipped, or if it's a tall tooltip)
      if (newTop + ttRect.height + margin > viewportHeight) {
        newTop = viewportHeight - ttRect.height - margin;
        // If tooltip is taller than viewport, pin to top after trying to fit at bottom
        if (newTop < margin) {
          newTop = margin;
          // TODO: Optionally, set maxHeight and overflow for very tall tooltips
          // tooltipElement.style.maxHeight = `${viewportHeight - 2 * margin}px`;
          // tooltipElement.style.overflowY = 'auto';
        }
      }

      // Check left boundary
      if (newLeft < margin) {
        newLeft = margin;
      }

      // Check right boundary
      if (newLeft + ttRect.width + margin > viewportWidth) {
        newLeft = viewportWidth - ttRect.width - margin;
      }

      // Final check for left boundary if tooltip is very wide and screen is narrow
      if (newLeft < margin) {
        newLeft = margin;
      }

      setTooltipStyles({
        opacity: 1,
        top: `${newTop}px`,
        left: `${newLeft}px`,
        position: 'fixed',
        zIndex: 50,
      });

    } else if (!isVisible) {
      // Only update styles if it's not already in the "hidden" state configuration
      if (tooltipStyles.opacity !== 0 || tooltipStyles.top !== '-9999px') {
        setTooltipStyles({
          opacity: 0,
          top: '-9999px',
          left: '-9999px',
          position: 'fixed',
          zIndex: 50,
        });
      }
    }
  }, [isVisible, text, iconSize, textColorClass, bgColorClass, tooltipStyles.opacity, tooltipStyles.top]); // include states read in conditions

  if (!text || !text.trim()) {
    return null;
  }

  return (
    <div
      className="inline-block" // Removed 'relative' and 'group' as they are no longer needed for this logic
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      ref={triggerRef}
    >
      <FaQuestionCircle
        size={iconSize}
        className={`cursor-help ${iconColorClass}`}
        onClick={() => setIsVisible(!isVisible)}
      />

      {text && text.trim() && (

        <div
          ref={tooltipRef}
          className={`
            px-3 py-2 rounded shadow-sm
            ${bgColorClass} ${textColorClass}
            text-sm text-center whitespace-normal break-words
            max-w-xs transition-opacity duration-200 ease-in-out
            pointer-events-none 
          `}
          style={tooltipStyles}
        >
          {text.split('\n').map((line, index) => (
            <React.Fragment key={index}>
              {line}
              <br />
            </React.Fragment>
          ))}       
           </div>
      )}
    </div>
  );
};
